<template>
  <div>
    <warehouse-purchase-item-dialog
      v-model="purchaseItemDialog"
      :warehouse="warehouse"
      @groceryAdded="addGrocery"
    />
    <warehouse-entry-item-dialog
      v-model="entryItemDialog"
      :warehouse="warehouse"
      @groceryAdded="addGrocery"
    />
    <warehouse-waybill-item-dialog
      v-model="waybillItemDialog"
      :warehouse="warehouse"
      :groceries="groceries"
      @groceryAdded="addGrocery"
    />
    <warehouse-writeoff-item-dialog
      v-model="writeoffItemDialog"
      :warehouse="warehouse"
      :groceries="groceries"
      @groceryAdded="addGrocery"
    />
    <warehouse-return-item-dialog
      v-model="returnItemDialog"
      :warehouse="warehouse"
      :groceries="groceries"
      @groceryAdded="addGrocery"
    />
    <global-confirm-box ref="confirmBox"></global-confirm-box>
    <v-row align="end" justify="space-between" class="mt-4 mb-0">
      <v-col cols="auto">
        <span class="headline black--text">{{ title }}</span>
      </v-col>
      <v-col>
        <v-row no-gutters align="center" justify="end">
          <text-field
            v-model="search"
            :placeholder="$t('search')"
            hide-details
            kind="search"
          />
        </v-row>
      </v-col>
    </v-row>
    <v-data-table
      :headers="headers"
      :items="filteredGroceries"
      item-key="id"
      class="striped-table ordera-box-shadow"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :footer-props="{
        'items-per-page-options': [5, 10, 25, 50, -1],
        'show-current-page': true,
        'show-first-last-page': true,
      }"
      :loading="loading.addGrocery || loading.removeGrocery"
    >
      <template v-slot:item.num="{ index }">
        <td>
          {{ (page - 1) * itemsPerPage + index + 1 }}
        </td>
      </template>

      <template v-slot:item.quantity="{ item }">
        <v-row no-gutters justify="end">
          <div style="width: 120px; max-width: 100%">
            <text-field
              v-model.number="item.quantity"
              type="number"
              :max="
                hasWarehouseStock(item)
                  ? item.stock || Number(item.warehouse_stock)
                  : undefined
              "
              min="0"
              :disabled="disabled"
              step="0.01"
              hide-details
              dense
              color="white"
              style="max-width: 150px"
            />
          </div>
        </v-row>
      </template>

      <template v-slot:item.warehouse_stock="{ item }">
        <span>
          {{ item.warehouse_stock || item.stockWithMeasure }}
        </span>
      </template>

      <template v-slot:item.actions="{ item }" v-if="!disabled">
        <remove-button @click="removeGrocery(item)" :disabled="loading.removeGrocery" />
      </template>

      <template v-slot:body.prepend v-if="!disabled">
        <warehouse-grocery-placeholder
          v-if="warehouse"
          :warehouse="warehouse"
          :type="placeholderType"
          :existing-groceries="groceries"
          :next-index="groceries.length + 1"
          @groceryAdded="addGrocery"
        />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import WarehousePurchaseItemDialog from "@/components/warehouse/requirement/purchase/WarehousePurchaseItemDialog.vue";
import WarehouseEntryItemDialog from "@/components/warehouse/entry/WarehouseEntryItemDialog.vue";
import WarehouseWaybillItemDialog from "@/components/warehouse/waybill/WarehouseWaybillItemDialog.vue";
import WarehouseWriteoffItemDialog from "@/components/warehouse/writeOff/dialogs/WarehouseWriteoffItemDialog.vue";
import WarehouseReturnItemDialog from "@/components/warehouse/return/dialogs/WarehouseReturnItemDialog.vue";
import WarehouseGroceryPlaceholder from "@/components/warehouse/requirement/WarehouseGroceryPlaceholder.vue";
import warehouseService from "@/services/warehouse";

export default {
  props: {
    groceries: {
      type: Array,
      required: true,
    },
    warehouse: {
      type: [Object, null, undefined],
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    waybill: {
      type: Boolean,
      default: false,
    },
    entry: {
      type: Boolean,
      default: false,
    },
    writeOff: {
      type: Boolean,
      default: false,
    },
    return: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    WarehousePurchaseItemDialog,
    WarehouseWaybillItemDialog,
    WarehouseWriteoffItemDialog,
    WarehouseReturnItemDialog,
    WarehouseEntryItemDialog,
    WarehouseGroceryPlaceholder,
  },
  data: () => ({
    search: "",
    purchaseItemDialog: false,
    waybillItemDialog: false,
    writeoffItemDialog: false,
    returnItemDialog: false,
    entryItemDialog: false,
    page: 1,
    itemsPerPage: 10,
    warehouseStatus: null,
    loading: {
      openDialog: false,
      addGrocery: false,
      removeGrocery: false,
      warehouseStatus: false,
    },
  }),
  methods: {
    async fetchWarehouseStatus() {
      if (!this.warehouse?.id) return;

      this.loading.warehouseStatus = true;
      try {
        const response = await warehouseService.getWarehouseStatus({
          warehouse_ids: this.warehouse.id,
          date: new Date().toISOString().split("T")[0],
        });

        this.warehouseStatus = response.data;
        this.updateGroceriesWithStock();
      } catch (error) {
        console.error("Error fetching warehouse status:", error);
      } finally {
        this.loading.warehouseStatus = false;
      }
    },
    updateGroceriesWithStock() {
      if (!this.warehouseStatus || !this.warehouseStatus.length) return;

      const warehouseData = this.warehouseStatus[0];
      if (!warehouseData?.groceries) return;

      // Create a map for quick lookup
      const stockMap = new Map(warehouseData.groceries.map((g) => [g.id, g.total]));

      // Update groceries with stock information
      this.groceries.forEach((grocery) => {
        const stock = stockMap.get(grocery.id);
        if (stock !== undefined) {
          this.$set(grocery, "warehouse_stock", stock);
          this.$set(grocery, "stockWithMeasure", `${stock} ${grocery.measure}`);
        }
      });
    },
    hasWarehouseStock(item) {
      return (
        (this.waybill || this.writeOff || this.return) &&
        item.warehouse_stock !== undefined
      );
    },
    openDialog() {
      if (this.loading.openDialog) {
        console.warn("Dialog opening operation already in progress");
        return;
      }

      if (!this.warehouse) {
        console.warn("Cannot open dialog without warehouse selection");
        return;
      }

      this.loading.openDialog = true;

      try {
        if (this.waybill) {
          this.waybillItemDialog = true;
        } else if (this.writeOff) {
          this.writeoffItemDialog = true;
        } else if (this.return) {
          this.returnItemDialog = true;
        } else if (this.entry) {
          this.entryItemDialog = true;
        } else {
          this.purchaseItemDialog = true;
        }
      } finally {
        this.loading.openDialog = false;
      }
    },
    addGrocery(grocery) {
      if (this.loading.addGrocery) {
        console.warn("Add grocery operation already in progress");
        return;
      }

      this.loading.addGrocery = true;

      try {
        const orderGrocery = this.groceries.find((item) => item.id === grocery.id);
        if (orderGrocery) {
          orderGrocery.quantity = grocery.quantity;
          this.purchaseItemDialog = false;
          return;
        }
        this.groceries.unshift(grocery);
        this.page = 1; // Reset to first page when adding new item
        this.purchaseItemDialog = false;
      } catch (error) {
        console.error("Error adding grocery:", error);
      } finally {
        this.loading.addGrocery = false;
      }
    },
    removeGrocery(item) {
      if (this.loading.removeGrocery) {
        console.warn("Remove grocery operation already in progress");
        return;
      }

      this.loading.removeGrocery = true;

      this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_grocery"),
          this.$t("warehouse.confirm_delete_grocery"),
          {
            color: "warning",
          }
        )
        .then(() => {
          this.$emit("removeGrocery", item);
        })
        .catch((err) => console.warn(err))
        .finally(() => {
          this.loading.removeGrocery = false;
        });
    },
  },
  computed: {
    title() {
      return this.waybill
        ? this.$t("goods_for_issuance")
        : this.writeOff
        ? this.$t("written_off_goods")
        : this.return
        ? this.$t("returned_goods")
        : this.entry
        ? this.$t("received_goods")
        : this.$t("ordered_goods");
    },
    placeholderType() {
      if (this.waybill) return "waybill";
      if (this.writeOff) return "writeoff";
      if (this.return) return "return";
      if (this.entry) return "entry";
      return "purchase";
    },
    isExitType() {
      return this.waybill || this.writeOff || this.return;
    },
    filteredGroceries() {
      if (!this.search) return this.groceries;
      const searchLower = this.search.toLowerCase();
      return this.groceries.filter((item) => {
        return (
          item.name?.toLowerCase().includes(searchLower) ||
          item.code?.toLowerCase().includes(searchLower)
        );
      });
    },
  },
  watch: {
    warehouse: {
      handler(newVal) {
        if (newVal?.id) {
          this.fetchWarehouseStatus();
        }
      },
      immediate: true,
    },
    groceries: {
      handler() {
        // Update stock when groceries change
        if (this.warehouseStatus) {
          this.updateGroceriesWithStock();
        }
      },
      deep: true,
    },
  },
};
</script>
